import { useState } from "react";
import { useAdminLatestBids } from "@/hooks/useAdminStats";
import type { AdminLatestBidItem } from "@/services/admin/adminStatsService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Gavel } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n";

const PAGE_SIZE = 10;

interface LatestBidsPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatDate(iso: string, locale: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(locale, {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function LatestBidsPopup({ open, onOpenChange }: LatestBidsPopupProps) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useAdminLatestBids(page, open);
  const { theme } = useTheme();
  const { language } = useLanguage();
  const lb =
    (
      adminLabels(language).stats as
        | { latestBids?: Record<string, string> }
        | undefined
    )?.latestBids ?? {};
  const locale = language === "en" ? "en-GB" : "sv-SE";
  const isDark = theme === "dark";

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasPrev = page > 1;
  const hasNext = page < totalPages;
  const pageInfo = (
    lb.pageInfo ?? "Sidan {page} av {total} · {count} bud totalt"
  )
    .replace("{page}", String(page))
    .replace("{total}", String(totalPages))
    .replace("{count}", String(total));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent nested className="w-[calc(100vw-1.5rem)] max-w-2xl max-h-[calc(100vh-2rem)] sm:max-h-[90vh] sm:w-full overflow-hidden flex flex-col rounded-2xl p-4 sm:p-5">
        <DialogHeader className="pb-1">
          <DialogTitle className="flex items-center gap-1.5 text-base sm:text-lg">
            <Gavel className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
            {lb.title ?? "Senaste buden"}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground mb-2">
          {lb.description ??
            "Alla bud, senaste först. 10 per sida. Högsta budet per auktion är markerat."}
        </p>
        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground text-xs">
            {lb.loading ?? "Laddar bud..."}
          </div>
        ) : !data?.items?.length ? (
          <div className="py-8 text-center text-muted-foreground text-xs">
            {lb.noBids ?? "Inga bud att visa."}
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-auto border rounded-md text-xs sm:text-sm">
              <Table>
                <TableHeader>
                  <TableRow className={cn(isDark && "border-border/50")}>
                    <TableHead className="w-[100px] sm:w-[120px] py-1.5 px-2 text-xs">
                      {lb.bidder ?? "Budare"}
                    </TableHead>
                    <TableHead className="w-[70px] sm:w-[80px] text-right py-1.5 px-2 text-xs">
                      {lb.amount ?? "Belopp"}
                    </TableHead>
                    <TableHead className="w-[70px] py-1.5 px-2 text-xs">
                      {lb.highest ?? "Högsta"}
                    </TableHead>
                    <TableHead className="w-[70px] py-1.5 px-2 text-xs">
                      {lb.regNo ?? "Reg nr"}
                    </TableHead>
                    <TableHead className="min-w-0 py-1.5 px-2 text-xs">
                      {lb.listingTitle ?? "Annonstitel"}
                    </TableHead>
                    <TableHead className="w-[85px] sm:w-[95px] text-muted-foreground py-1.5 px-2 text-xs whitespace-nowrap">
                      {lb.time ?? "Tid"}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((row: AdminLatestBidItem) => (
                    <TableRow
                      key={row.id}
                      className={cn(isDark && "border-border/50")}
                    >
                      <TableCell className="py-1.5 px-2">
                        <div
                          className="font-medium text-xs truncate max-w-[100px] sm:max-w-[120px]"
                          title={row.bidderName || undefined}
                        >
                          {row.bidderName || "—"}
                        </div>
                        {row.bidderEmail && (
                          <div
                            className="text-[10px] sm:text-xs text-muted-foreground truncate max-w-[100px] sm:max-w-[120px]"
                            title={row.bidderEmail}
                          >
                            {row.bidderEmail}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium py-1.5 px-2 text-xs">
                        {row.amount.toLocaleString(locale)}{" "}
                        {lb.currency ?? "kr"}
                      </TableCell>
                      <TableCell className="py-1.5 px-2">
                        {row.isHighestBid ? (
                          <span className="inline-flex items-center rounded bg-green-600 px-1.5 py-0.5 text-[10px] font-medium text-white dark:bg-green-500 dark:text-green-950">
                            {lb.yes ?? "Ja"}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-[10px]">
                            {lb.no ?? "Nej"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono py-1.5 px-2 text-[10px] sm:text-xs">
                        {row.registrationNo || "—"}
                      </TableCell>
                      <TableCell
                        className="min-w-0 max-w-[120px] sm:max-w-[160px] truncate py-1.5 px-2 text-xs"
                        title={row.listingTitle}
                      >
                        {row.listingTitle || "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-[10px] sm:text-xs py-1.5 px-2 whitespace-nowrap">
                        {formatDate(row.createdAt, locale)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between gap-2 py-1.5 border-t">
              <span className="text-[10px] sm:text-xs text-muted-foreground">
                {pageInfo}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs px-2"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!hasPrev}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  {lb.prev ?? "Föregående"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs px-2"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={!hasNext}
                >
                  {lb.next ?? "Nästa"}
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
